import { useCallback, useEffect, useState } from "react";

import { AvailablePaymentCurrency, formatCurrencyFromCents } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { isBidOutbid, isCurrentUserTopBidder } from "./bid-status.helper";

import { HighestProductBidsPerRateObject } from "@/common/contexts/FrontendAuctionContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { RateOutput } from "@/common/types/trpc";

export type BidMode = "single" | "auto";

interface UseDrawerBidStateParams {
  rate: RateOutput;
  form: UseFormReturn<TicketingPaymentInput>;
  rateData: HighestProductBidsPerRateObject | undefined;
  currentUserCommandIds: readonly string[] | undefined;
  minimumBidAmountInCents: number;
  formCurrency: AvailablePaymentCurrency;
  onClose: () => void;
  isBiddingBlocked: boolean;
}

export interface UseDrawerBidStateResult {
  bidAmountInCents: number | null;
  bidMode: BidMode;
  submitted: boolean;
  isEditingBid: boolean;
  inputError: string | undefined;
  isAlreadyOutbidWhileEditing: boolean;
  handleSubmit: () => Promise<void>;
  handleBidAmountChange: (amountInCents: number | null) => void;
  handleBidModeChange: (mode: BidMode) => void;
  handleEditBid: () => void;
  handleCancelBid: () => void;
}

export function useDrawerBidState({
  rate,
  form,
  rateData,
  currentUserCommandIds,
  minimumBidAmountInCents,
  formCurrency,
  onClose,
  isBiddingBlocked,
}: UseDrawerBidStateParams): UseDrawerBidStateResult {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();

  const existingFormBid = form.watch(`ticketsPurchased.${rate.sortIndex}.bid`) as number | undefined;
  const existingAutoBidMax = form.watch(`ticketsPurchased.${rate.sortIndex}.autoBidMaxAmount`) as number | undefined;
  const hasExistingFormBid = Boolean(existingFormBid && existingFormBid > 0);
  const hasExistingAutoBid = Boolean(existingAutoBidMax && existingAutoBidMax > 0);
  const userEffectiveMaxBid: number | null = hasExistingAutoBid
    ? existingAutoBidMax!
    : hasExistingFormBid
      ? existingFormBid!
      : null;

  const highestBidAmount = rateData?.amount;
  const userIsTopBidder = isCurrentUserTopBidder(rateData, currentUserCommandIds);
  const isUserOutbid = isBidOutbid(userEffectiveMaxBid, highestBidAmount, userIsTopBidder);

  const [bidAmountInCents, setBidAmountInCents] = useState<number | null>(userEffectiveMaxBid);
  const [inputError, setInputError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(hasExistingFormBid && !isUserOutbid);
  const [isEditingBid, setIsEditingBid] = useState(hasExistingFormBid && isUserOutbid);
  const [bidMode, setBidMode] = useState<BidMode>(hasExistingAutoBid ? "auto" : "single");

  // If the server reports the user got outbid after they submitted, drop back into edit mode.
  useEffect(() => {
    if (submitted && !isEditingBid && isUserOutbid) {
      setSubmitted(false);
      setIsEditingBid(true);
    }
  }, [submitted, isEditingBid, isUserOutbid]);

  const handleSubmit = useCallback(async () => {
    if (isBiddingBlocked) {
      return;
    }

    if (bidAmountInCents === null || bidAmountInCents < minimumBidAmountInCents) {
      const formattedMinimum = formatCurrencyFromCents(minimumBidAmountInCents, {
        isoLocale,
        currency: formCurrency,
      });
      setInputError(t("ticketing", "auction.input.minError", { amount: formattedMinimum }));
      return;
    }

    setInputError(undefined);

    const areAnswersValid = await form.trigger(`customAnswersOfParticipants.${rate.sortIndex}`, {
      shouldFocus: true,
    });
    if (!areAnswersValid) {
      return;
    }

    if (bidMode === "auto") {
      form.setValue(`ticketsPurchased.${rate.sortIndex}.bid`, minimumBidAmountInCents);
      form.setValue(`ticketsPurchased.${rate.sortIndex}.autoBidMaxAmount`, bidAmountInCents);
    } else {
      form.setValue(`ticketsPurchased.${rate.sortIndex}.bid`, bidAmountInCents);
      form.setValue(`ticketsPurchased.${rate.sortIndex}.autoBidMaxAmount`, undefined);
    }
    form.setValue(`ticketsPurchased.${rate.sortIndex}.rateId`, rate.id);

    setSubmitted(true);
    setIsEditingBid(false);
  }, [isBiddingBlocked, bidAmountInCents, minimumBidAmountInCents, isoLocale, formCurrency, t, form, rate, bidMode]);

  const handleBidAmountChange = useCallback(
    (amountInCents: number | null) => {
      setBidAmountInCents(amountInCents);
      if (inputError) {
        setInputError(undefined);
      }
    },
    [inputError]
  );

  const handleBidModeChange = useCallback((mode: BidMode) => {
    setBidMode(mode);
    setBidAmountInCents(null);
    setInputError(undefined);
  }, []);

  const handleEditBid = useCallback(() => {
    setSubmitted(false);
    setIsEditingBid(true);
  }, []);

  const handleCancelBid = useCallback(() => {
    form.setValue(`ticketsPurchased.${rate.sortIndex}.bid`, undefined);
    form.setValue(`ticketsPurchased.${rate.sortIndex}.autoBidMaxAmount`, undefined);
    setBidAmountInCents(null);
    setInputError(undefined);
    setSubmitted(false);
    setIsEditingBid(false);
    setBidMode("single");
    onClose();
  }, [form, rate.sortIndex, onClose]);

  const isAlreadyOutbidWhileEditing = isEditingBid && isBidOutbid(bidAmountInCents, highestBidAmount, userIsTopBidder);

  return {
    bidAmountInCents,
    bidMode,
    submitted,
    isEditingBid,
    inputError,
    isAlreadyOutbidWhileEditing,
    handleSubmit,
    handleBidAmountChange,
    handleBidModeChange,
    handleEditBid,
    handleCancelBid,
  };
}
